import Dexie, {} from 'dexie';
import { entityRowFromPayload, parseLegacyShiftAggregateEnvelope } from './entity.js';
export const LOCAL_DB_SCHEMA_VERSION = 3;
export const PROFILE_DIRECTORY_DATABASE_NAME = 'dmf-local-profile-directory-v1';
export const PROFILE_INSTALLATION_SLOT = 'primary';
const LEGACY_V1_STORES = {
    shift_aggregates: '&shift_id,dataset_epoch,revision,updated_at',
    outbox: '&operation_id,&idempotency_key_sha256,[device_id+dataset_epoch+local_sequence],dataset_epoch,created_at',
    drafts: '&draft_id,&[local_profile_id+draft_key],local_profile_id,dataset_epoch,updated_at',
    profiles: '&key',
    runtime_state: '&key,dataset_epoch',
    device_cursors: '&device_id,dataset_epoch,registration_status,registration_expires_at',
    trip_revisions: '&trip_revision_id,shift_id,trip_segment_id,dataset_epoch,recorded_at',
    migration_evidence: '&migration_id,migrated_at',
};
const ENTITY_STORE = '[entity_type+entity_id],entity_type,entity_id,dataset_epoch,revision,updated_at,tombstone_deleted_at';
function dexieOptions(dependencies) {
    return dependencies === undefined
        ? { autoOpen: false, cache: 'cloned' }
        : {
            autoOpen: false,
            cache: 'cloned',
            indexedDB: dependencies.indexedDB,
            IDBKeyRange: dependencies.IDBKeyRange,
        };
}
export class ProfileDatabase extends Dexie {
    entities;
    outbox;
    drafts;
    profiles;
    runtime_state;
    device_cursors;
    trip_revisions;
    migration_evidence;
    constructor(databaseName, dependencies) {
        super(databaseName, dexieOptions(dependencies));
        this.version(1).stores(LEGACY_V1_STORES);
        this.version(2)
            .stores({ entities: ENTITY_STORE })
            .upgrade(async (transaction) => migrateLegacyAggregates(transaction));
        this.version(3).stores({ shift_aggregates: null });
    }
}
async function migrateLegacyAggregates(transaction) {
    const legacyRows = await transaction
        .table('shift_aggregates')
        .toArray();
    if (legacyRows.length === 0) {
        return;
    }
    await transaction.table('entities').bulkPut(legacyRows.map((row) => {
        const legacy = parseLegacyShiftAggregateEnvelope(row);
        return entityRowFromPayload({ entity_type: 'shift_aggregate', value: legacy.aggregate }, legacy.dataset_epoch);
    }));
}
export class ProfileDirectoryDatabase extends Dexie {
    profiles;
    bootstrap_intents;
    replacement_checkpoints;
    replacement_executions;
    migration_intents;
    wipe_intents;
    constructor(dependencies) {
        super(PROFILE_DIRECTORY_DATABASE_NAME, dexieOptions(dependencies));
        this.version(1).stores({
            profiles: '&local_profile_id,&logical_database_name,&active_database_name,dataset_epoch,schema_version',
        });
        this.version(2)
            .stores({
            profiles: '&local_profile_id,&installation_slot,&logical_database_name,&active_database_name,dataset_epoch,schema_version',
        })
            .upgrade(async (transaction) => {
            const profiles = transaction.table('profiles');
            if ((await profiles.count()) > 1) {
                throw new Error('profile directory contains multiple legacy installation profiles');
            }
            await profiles.toCollection().modify((entry) => {
                Object.assign(entry, {
                    installation_slot: PROFILE_INSTALLATION_SLOT,
                    staged_database_names: [],
                });
            });
        });
        this.version(3).stores({
            replacement_checkpoints: '&checkpoint_id,local_profile_id,dataset_epoch,created_at',
        });
        this.version(4).stores({
            wipe_intents: '&local_profile_id,created_at',
        });
        this.version(5).stores({
            bootstrap_intents: '&local_profile_id,&installation_slot,&physical_database_name,dataset_epoch,created_at',
        });
        this.version(6).stores({
            replacement_executions: '&replacement_id,local_profile_id,[local_profile_id+replacement_epoch],status,prepared_at',
        });
        this.version(7).stores({
            migration_intents: '&local_profile_id,&migration_id,source_database_name,created_at',
        });
    }
}
export function physicalGenerationDatabaseName(logicalDatabaseName, generation, generationId) {
    return `${logicalDatabaseName}--generation-${String(generation)}-${generationId}`;
}
export { ENTITY_STORE, LEGACY_V1_STORES };
//# sourceMappingURL=database.js.map